import { TooltipWrapper } from 'components/TooltipWrapper';
import { RequestStatus } from 'features/request/requestSlice';
import type { DatePickerPropOnChange } from 'uikit';
import {
    Button,
    IconClose,
    IconSearch,
    TextField,
    Text,
    IconWatchFilled,
    ProgressStepBar,
    Badge,
    DatePicker,
    Modal,
    Loader,
} from 'uikit';

import { useCallback, useMemo, useState } from 'react';

import { useStoringHistoryData } from './hooks/useStoringHistoryData';

import Styles from './StoringHistoryChange.module.css';

export const StoringHistoryChange = ({ contractData }: { contractData?: Record<string, any> }) => {
    const [isOpenModal, setIsOpenModal] = useState(false);
    const [dateValue, setDateValue] = useState<[Date?, Date?] | null>([]);

    const onChange: DatePickerPropOnChange<'date-range'> = ({ value: newValue }) => {
        setDateValue(newValue);
    };

    const handleOnClick = useCallback(() => {
        setIsOpenModal((prevState) => !prevState);
    }, []);

    const { dataItems, contractLogEntriesStatus, searchString, handleChangeSearchString, clearSearchValue } =
        useStoringHistoryData(contractData?.id, dateValue, isOpenModal);

    const isLoading = useMemo(() => contractLogEntriesStatus === RequestStatus.FETCHING, [contractLogEntriesStatus]);

    const steps = useMemo(
        () =>
            dataItems?.map((item) => ({
                label: item?.date,
                content: (
                    <div key={item.id}>
                        <Text size="s" view="secondary">
                            {item?.userLogin}
                        </Text>
                        <Badge className={Styles.Badge} size="s" status="system" label={item?.entityDisplayName} />
                    </div>
                ),
                lineStatus: 'normal',
                status: 'normal',
            })),
        [dataItems],
    );

    const handleClose = useCallback(() => {
        setIsOpenModal(false);
        setDateValue([]);
        clearSearchValue();
    }, [clearSearchValue]);

    return (
        <>
            <div className={Styles.wrapButtonTooltip}>
                <TooltipWrapper
                    direction="leftCenter"
                    message="Посмотреть историю изменений"
                    size="m"
                    isInteractive={false}
                >
                    <Button
                        view="secondary"
                        style={{ width: 32, height: 32 }}
                        onClick={handleOnClick}
                        iconSize="s"
                        iconLeft={IconWatchFilled}
                        onlyIcon
                    />
                </TooltipWrapper>
            </div>

            <Modal
                isOpen
                hasOverlay={false}
                className={`${Styles.mainWrapper} ${isOpenModal ? Styles.active : ''}`}
                onClickOutside={handleClose}
            >
                <div className={Styles.header}>
                    <Text view="secondary" lineHeight="l" size="s" weight="semibold" transform="uppercase">
                        просмотр истории изменений
                    </Text>
                    <Button
                        onClick={handleClose}
                        label="Закрыть"
                        iconSize="s"
                        iconLeft={IconClose}
                        view="clear"
                        onlyIcon
                    />
                </div>
                <div className={Styles.Content}>
                    <TextField
                        className={Styles.Search}
                        onChange={handleChangeSearchString}
                        value={searchString}
                        type="text"
                        placeholder="Найти в списке"
                        size="s"
                        leftSide={IconSearch}
                    />
                    <DatePicker
                        value={dateValue}
                        onChange={onChange}
                        type="date-range"
                        withClearButton
                        size="s"
                        className={Styles.DataPicker}
                    />
                    {isLoading && (
                        <div className={Styles.WrapLoader}>
                            <Loader />
                        </div>
                    )}
                    {!!steps?.length && !!dataItems?.length && !isLoading && (
                        <ProgressStepBar key={dataItems?.length} steps={steps} direction="vertical" />
                    )}
                </div>
            </Modal>
        </>
    );
};

StoringHistoryChange.displayName = 'StoringHistoryChange';
